import { Mail, MapPin, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useEffect, useRef, useState } from 'react';
import { useToast } from "@/hooks/use-toast";
import emailjs from '@emailjs/browser';

const ContactSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const { toast } = useToast();
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    
    return () => {
      observer.disconnect();
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const serviceId = "service_xlpha6b";
      const templateId = "template_x8oa4pq";
      const publicKey = "ANl95mHO0dimhamNx";

      const result = await emailjs.sendForm(
        serviceId,
        templateId,
        formRef.current!,
        publicKey
      );

      console.log('Email sent successfully:', result.text);
      toast({
        title: "Message Sent!",
        description: "Thanks for reaching out. I'll get back to you soon!",
      });
      
      if (formRef.current) {
        formRef.current.reset();
      }
    } catch (error) {
      console.error('Failed to send email:', error);
      toast({
        title: "Failed to Send",
        description: "Sorry, there was an error sending your message. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" ref={sectionRef} className="relative">
      <div className="contact-vector contact-vector-1"></div>
      <div className="contact-vector contact-vector-2"></div>
      
      <div className="section-container relative z-10">
        <div className="max-w-5xl mx-auto">
          <div className={`text-center mb-12 transition-all duration-500 ${
            isVisible ? 'opacity-100 translate-y-0 fade-up' : 'opacity-0 translate-y-8'
          }`}>
            <p className="heading-highlight mb-2 animate-pulse-slow">Get In Touch</p>
            <h2 className="text-3xl font-bold mb-4">Let's Connect</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              I'm always happy to connect. If you'd like to discuss frontend architecture, share ideas on what you're building, or just want to say hi, feel free to reach out.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            <div className={`lg:col-span-2 space-y-6 transition-all duration-500 delay-[200ms] ${
              isVisible ? 'opacity-100 translate-x-0 slide-right' : 'opacity-0 -translate-x-8'
            }`}>
              <div className="flex items-start gap-4 hover:scale-105 transition-all duration-300">
                <div className="p-3 rounded-full bg-secondary/50 border border-border/50 animate-float">
                  <Mail className="h-5 w-5 text-accent" />
                </div>
                <div>
                  <h3 className="font-medium mb-1">Email</h3>
                  <a 
                    href="mailto:aniketkasale02@gmail.com" 
                    className="text-muted-foreground hover:text-accent transition-colors"
                  >
                    aniketkasale02@gmail.com
                  </a>
                </div>
              </div>
              
              <div className="flex items-start gap-4 hover:scale-105 transition-all duration-300" style={{ animationDelay: '200ms' }}>
                <div className="p-3 rounded-full bg-secondary/50 border border-border/50 animate-float">
                  <MapPin className="h-5 w-5 text-accent" />
                </div>
                <div>
                  <h3 className="font-medium mb-1">Location</h3>
                  <p className="text-muted-foreground">Pune, Maharashtra, India</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4 hover:scale-105 transition-all duration-300" style={{ animationDelay: '400ms' }}>
                <div className="p-3 rounded-full bg-secondary/50 border border-border/50 animate-float">
                  <Phone className="h-5 w-5 text-accent" />
                </div>
                <div>
                  <h3 className="font-medium mb-1">Connect</h3>
                  <a 
                    href="https://www.linkedin.com/in/aniket-kasale/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-accent transition-colors"
                  >
                    LinkedIn Profile
                  </a>
                </div>
              </div>
            </div>
            
            <div className={`lg:col-span-3 transition-all duration-500 delay-[400ms] ${
              isVisible ? 'opacity-100 translate-x-0 slide-left' : 'opacity-0 translate-x-8'
            }`}>
              <form ref={formRef} className="space-y-4" onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="user_name" className="block text-sm font-medium mb-1">
                      Name
                    </label>
                    <Input 
                      id="user_name" 
                      name="user_name" 
                      placeholder="Your name" 
                      required 
                      className="focus:border-accent focus:ring-accent" 
                    />
                  </div>
                  <div>
                    <label htmlFor="user_email" className="block text-sm font-medium mb-1">
                      Email
                    </label>
                    <Input 
                      id="user_email" 
                      name="user_email" 
                      type="email" 
                      placeholder="Your email" 
                      required 
                      className="focus:border-accent focus:ring-accent" 
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium mb-1">
                    Subject
                  </label>
                  <Input 
                    id="subject" 
                    name="subject" 
                    placeholder="Subject" 
                    required 
                    className="focus:border-accent focus:ring-accent" 
                  />
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-medium mb-1">
                    Message
                  </label>
                  <Textarea 
                    id="message" 
                    name="message" 
                    placeholder="Your message" 
                    rows={6} 
                    required 
                    className="focus:border-accent focus:ring-accent"
                  />
                </div>
                
                <Button 
                  type="submit" 
                  size="lg" 
                  className="w-full hover:scale-[1.02] transition-transform animate-float"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
